import { useMemo, useState } from 'react'
import BackofficePanel from '../BackofficePanel'
import { detailFor, iconFor, relativeTime, titleFor } from '../feedText'
import { applyMute, type EventFeedController, type FeedMute } from '../../../app/backoffice/useEventFeed'
import type { FeedEventView } from '../../../adminApi'

/**
 * The feed panel.
 *
 * The toast is ephemeral; **this is the record.** Reverse-chronological, grouped by day, filterable
 * by scope and type, and every row carries its source badge — a hand-entered result must be
 * visually distinct from a provider one, forever.
 *
 * The connection state is in the header on purpose: "live" and "polling" behave identically until
 * something goes wrong, and an operator staring at a feed with no events needs to know which of
 * "nothing is happening" and "nothing is arriving" they are looking at.
 */

const MUTE_LABELS: Record<FeedMute, string> = {
  none: 'Everything',
  'system-only': 'System only',
  'match-only': 'Match only',
  all: 'Muted',
}

const STATUS_LABEL = {
  live: 'live',
  polling: 'polling',
  connecting: 'connecting…',
  disconnected: 'disconnected',
} as const

/** Rendered past ~200 rows the list scrolls rather than growing the page — see `.bo-list`. */
const VISIBLE_ROWS = 200

function dayKey(iso: string): string {
  return iso.slice(0, 10)
}

export default function EventFeedPanel({ feed, nowMs }: { feed: EventFeedController; nowMs: number }) {
  const [scope, setScope] = useState<'all' | 'match' | 'system' | 'admin'>('all')
  const [type, setType] = useState<string>('all')

  const filtered = useMemo(() => {
    const muted = applyMute(feed.events, feed.mute)
    return muted
      .filter((event) => {
        if (scope === 'all') return true
        if (scope === 'admin') return event.type === 'admin-action'
        return event.scope === scope
      })
      .filter((event) => type === 'all' || event.type === type)
      .slice()
      .sort((a, b) => b.id - a.id)
      .slice(0, VISIBLE_ROWS)
  }, [feed.events, feed.mute, scope, type])

  const types = useMemo(
    () => [...new Set(feed.events.map((event) => event.type))].sort(),
    [feed.events]
  )

  return (
    <BackofficePanel
      title="Event feed"
      badge={STATUS_LABEL[feed.status]}
      subtitle={
        <>
          last event #{feed.cursor}
          {feed.truncated && ' · replay was truncated — older events are on the server only'}
          {feed.error && ` · ${feed.error}`}
        </>
      }
    >
      <div className="bo-filters">
        <select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)} aria-label="Scope">
          <option value="all">All scopes</option>
          <option value="match">Match</option>
          <option value="system">System</option>
          <option value="admin">Admin actions</option>
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Event type">
          <option value="all">All types</option>
          {types.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <select
          value={feed.mute}
          onChange={(e) => feed.setMute(e.target.value as FeedMute)}
          aria-label="Toasts"
        >
          {(Object.keys(MUTE_LABELS) as FeedMute[]).map((value) => (
            <option key={value} value={value}>
              Toasts: {MUTE_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <p className="bo-empty">No events yet. A kick-off, a goal or a sync failure lands here.</p>
      ) : (
        <div className="bo-list">
          {filtered.map((event, index) => (
            <FeedRow
              key={event.id}
              event={event}
              nowMs={nowMs}
              showDay={index === 0 || dayKey(event.createdAt) !== dayKey(filtered[index - 1].createdAt)}
            />
          ))}
        </div>
      )}
    </BackofficePanel>
  )
}

function FeedRow({
  event,
  nowMs,
  showDay,
}: {
  event: FeedEventView
  nowMs: number
  showDay: boolean
}) {
  const detail = detailFor(event)
  return (
    <>
      {showDay && <p className="bo-day">{dayKey(event.createdAt)}</p>}
      <div className="bo-feed-row">
        {/* Relative, because the judgement is always "how long ago"; the absolute instant is on
            hover for the one time in ten someone needs it. */}
        <span className="bo-feed-row__time" title={event.createdAt}>
          {relativeTime(event.createdAt, nowMs)}
        </span>
        <span aria-hidden="true">{iconFor(event.type)}</span>
        <span className="bo-feed-row__text">
          {titleFor(event)}
          {detail && <span className="bo-mono"> {detail}</span>}
        </span>
        {event.source !== 'provider' && (
          <span className={`bo-tag ${event.source === 'manual' ? 'bo-tag--manual' : ''}`}>
            {event.source}
          </span>
        )}
      </div>
    </>
  )
}
